export interface HoldingSegment {
  person: string;
  fromTs: Date;
  toTs: Date;
  daysHeld: number;
  daysHeldPastDue: number;
}

export interface ItemForHolding {
  id: string;
  assignedTo: string;
  currentResponsible?: string | null;
  createdAt: Date | string;
  dueDate?: string | null;
  originalDueDate?: string | null;
  status: string;
}

export interface EventForHolding {
  eventType: string;
  payload: unknown;
  createdAt: Date | string;
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

function toDate(v: Date | string): Date {
  return v instanceof Date ? v : new Date(v);
}

function dueDateAsDate(dueIso: string | null | undefined): Date | null {
  if (!dueIso) return null;
  return new Date(dueIso + "T23:59:59.999Z");
}

function isHandoffLikeEvent(ev: EventForHolding): boolean {
  if (ev.eventType === "RESPONSIBILITY_HANDOFF") return true;
  if (ev.eventType === "RETURNED_FOR_AMENDMENTS") return true;
  if (ev.eventType === "STATUS_CHANGED") {
    const p = ev.payload as { to_responsible?: string } | null;
    return !!p?.to_responsible;
  }
  if (ev.eventType === "FIELD_UPDATED") {
    const p = ev.payload as { field?: string; new_value?: string } | null;
    return p?.field === "current_responsible" && !!p?.new_value;
  }
  return false;
}

function nextHolder(ev: EventForHolding): string | null {
  const p = ev.payload as
    | { to_responsible?: string; return_to?: string; field?: string; new_value?: string }
    | null;
  if (!p) return null;
  if (ev.eventType === "RETURNED_FOR_AMENDMENTS") {
    return p.return_to || p.to_responsible || null;
  }
  if (ev.eventType === "FIELD_UPDATED" && p.field === "current_responsible") {
    return p.new_value || null;
  }
  return p.to_responsible || null;
}

function findCloseEvent(events: EventForHolding[]): EventForHolding | null {
  const closes = events
    .filter((e) => e.eventType === "STATUS_CHANGED")
    .filter((e) => {
      const p = e.payload as { new_status?: string; to?: string } | null;
      return (
        p?.new_status === "CLOSED" ||
        p?.to === "CLOSED" ||
        p?.new_status === "SENT_UPWARD"
      );
    })
    .sort(
      (a, b) =>
        toDate(b.createdAt).getTime() - toDate(a.createdAt).getTime()
    );
  return closes[0] || null;
}

function diffDays(from: Date, to: Date): number {
  return Math.max(0, (to.getTime() - from.getTime()) / MS_PER_DAY);
}

function daysPastDueWithin(from: Date, to: Date, due: Date | null): number {
  if (!due) return 0;
  const start = from.getTime() > due.getTime() ? from : due;
  if (to.getTime() <= start.getTime()) return 0;
  return (to.getTime() - start.getTime()) / MS_PER_DAY;
}

export function computeHoldingSegments(
  item: ItemForHolding,
  events: EventForHolding[],
  asOf: Date = new Date()
): HoldingSegment[] {
  const due = dueDateAsDate(item.dueDate);
  const sorted = events
    .filter(isHandoffLikeEvent)
    .slice()
    .sort(
      (a, b) =>
        toDate(a.createdAt).getTime() - toDate(b.createdAt).getTime()
    );

  const closeEv = findCloseEvent(events);
  const endTs = closeEv ? toDate(closeEv.createdAt) : asOf;

  const segments: HoldingSegment[] = [];
  let currentHolder = item.assignedTo;
  let currentStart = toDate(item.createdAt);

  for (const ev of sorted) {
    const evTs = toDate(ev.createdAt);
    if (evTs.getTime() > endTs.getTime()) break;
    const next = nextHolder(ev);
    if (!next || next === currentHolder) continue;
    const segEnd = evTs;
    const daysHeld = diffDays(currentStart, segEnd);
    segments.push({
      person: currentHolder,
      fromTs: currentStart,
      toTs: segEnd,
      daysHeld: Math.round(daysHeld * 10) / 10,
      daysHeldPastDue:
        Math.round(daysPastDueWithin(currentStart, segEnd, due) * 10) / 10,
    });
    currentHolder = next;
    currentStart = segEnd;
  }

  if (endTs.getTime() > currentStart.getTime()) {
    const daysHeld = diffDays(currentStart, endTs);
    segments.push({
      person: currentHolder,
      fromTs: currentStart,
      toTs: endTs,
      daysHeld: Math.round(daysHeld * 10) / 10,
      daysHeldPastDue:
        Math.round(daysPastDueWithin(currentStart, endTs, due) * 10) / 10,
    });
  }

  return segments;
}

export function getHolderAtClose(
  item: ItemForHolding,
  events: EventForHolding[]
): string | null {
  const closeEv = findCloseEvent(events);
  if (!closeEv) return null;
  const closeTs = toDate(closeEv.createdAt).getTime();

  const sorted = events
    .filter(isHandoffLikeEvent)
    .filter((e) => toDate(e.createdAt).getTime() < closeTs)
    .slice()
    .sort(
      (a, b) =>
        toDate(a.createdAt).getTime() - toDate(b.createdAt).getTime()
    );

  let holder = item.assignedTo;
  for (const ev of sorted) {
    const next = nextHolder(ev);
    if (next) holder = next;
  }
  return holder || null;
}

export function getCurrentHolderName(item: ItemForHolding): string {
  return item.currentResponsible || item.assignedTo;
}
